import { addToChainAfter } from "@insite/client-framework/HandlerCreator";
import { chain, PopulateApiParameter } from "@insite/client-framework/Store/CommonHandlers/ShareProduct";
import { MGBW_IMAGE_URL_MEDIUM } from "@tSurya/Common/MgbwPropertyKeys";
import { applyCylindoFeaturesToUrl } from "@tSurya/Common/Utilities/cylindoViewer";
import { appendSelectionsToUrl } from "@tSurya/Handlers/Configurator/ConfiguratorUrlUtils";
import {
    getConfiguratorSelections,
    getCylindoConfig,
    isConfiguratorProduct,
} from "@tSurya/Store/Data/ConfiguratorSelectors";
import CustomApplicationState from "@tSurya/Store/Data/CustomApplicationState/CustomApplicationState";

/**
 * For MGBW configurable products, rewrites the Share ("Tell a Friend") payload so that:
 * - the shared link points at the active configuration URL (product detail path with the
 *   "oid-" selection query params appended), and
 * - the shared image reflects the active Cylindo configuration (the default Cylindo medium
 *   image re-skinned with the current selection features) instead of the generic product image.
 *
 * No-op for non-configurable products, so standard products keep their default share behavior.
 */
const ApplyConfiguratorShareData: typeof PopulateApiParameter = props => {
    const product = props.parameter.product;
    const model = props.apiParameter?.tellAFriendModel;
    if (!product || !model || !isConfiguratorProduct(product)) {
        return;
    }

    const state = props.getState() as CustomApplicationState;
    const selections = getConfiguratorSelections(state);
    const cylindoConfig = getCylindoConfig(state);

    // Share the active configuration URL by appending the current selections as "oid-" params.
    const productUrl =
        selections && Object.keys(selections).length > 0
            ? appendSelectionsToUrl(props.parameter.productDetailPath, selections)
            : model.productUrl;

    // Share the Cylindo image for the active configuration (falls back to the default Cylindo
    // medium image when no selection features are present).
    const baselineMediumImage = product.properties?.[MGBW_IMAGE_URL_MEDIUM];
    const productImage = baselineMediumImage
        ? cylindoConfig?.features
            ? applyCylindoFeaturesToUrl(baselineMediumImage, cylindoConfig.features)
            : baselineMediumImage
        : model.productImage;

    props.apiParameter = {
        tellAFriendModel: { ...model, productUrl, productImage },
    };
};

addToChainAfter(chain, PopulateApiParameter, ApplyConfiguratorShareData);
